import { useEffect, useState } from 'react';
import { Icon } from '@gftg/ui';
import { useSearchParams } from 'react-router';
import { FilterChip } from '../components/FilterChip';
import { SessionCard } from '../components/SessionCard';
import {
  EmptyBlock,
  ErrorBlock,
  ICON_BUTTON,
  LoadingBlock,
  PageHeader,
} from '../components/states';
import { calendarDateIn, formatDayHeading, formatMonthDay, formatShortDay } from '../lib/datetime';
import { defaultProgramDayKey } from '../lib/eventPhase';
import { useEvent, useProgram, type Session } from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';
import { useNow } from '../lib/useNow';

export function ProgramPage() {
  const event = useEvent();
  const now = useNow(event.data?.serverTime);
  const [params, setParams] = useSearchParams();

  const dayParam = params.get('day') ?? undefined;
  const trackId = params.get('track') ?? undefined;
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [searchOpen, setSearchOpen] = useState(search.length > 0);
  const keyword = useDebounced(search, 250);

  // PRG-04: during the event the program opens at the current day rather than at day one.
  useEffect(() => {
    if (!event.data || dayParam || keyword) {
      return;
    }
    const key = defaultProgramDayKey(event.data, now);
    if (key) {
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        next.set('day', key);
        return next;
      });
    }
    // `now` deliberately omitted: the day should be chosen once, not reset while reading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.data, dayParam, keyword, setParams]);

  // Searching looks across the whole program, not only the day being viewed (PRG-03).
  const program = useProgram({
    ...(keyword ? { q: keyword } : dayParam ? { day: dayParam } : {}),
    ...(trackId ? { trackId } : {}),
  });

  const update = (key: string, value: string | undefined) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  if (event.isPending || program.isPending) {
    return <LoadingBlock label="Loading the agenda…" />;
  }

  if (event.isError || !event.data || program.isError || !program.data) {
    return (
      <ErrorBlock
        message="The agenda could not be loaded."
        onRetry={() => {
          void event.refetch();
          void program.refetch();
        }}
      />
    );
  }

  const { timezone, days } = event.data;
  const today = calendarDateIn(timezone, now);
  const visibleDays = program.data.days.filter((day) => day.sessions.length > 0);

  return (
    <>
      {/* The header and the filters stay put while the list moves under them, as in the design:
          on a four-day agenda, losing the day you are looking at costs a scroll back up. */}
      <div className="sticky top-0 z-10 bg-canvas lg:top-20">
        <PageHeader
          title="Agenda"
          action={
            <button
              type="button"
              onClick={() => {
                if (searchOpen) {
                  setSearch('');
                  update('q', undefined);
                }
                setSearchOpen(!searchOpen);
              }}
              aria-label={searchOpen ? 'Close search' : 'Search the agenda'}
              aria-expanded={searchOpen}
              className={ICON_BUTTON}
            >
              <Icon name={searchOpen ? 'chevron-right' : 'search'} size={17} />
            </button>
          }
        />

        <div className="px-gutter space-y-2.5 pb-3">
          {searchOpen && (
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(item) => {
                setSearch(item.target.value);
                update('q', item.target.value || undefined);
              }}
              placeholder="Search sessions and speakers"
              aria-label="Search the agenda"
              className="text-body rounded-button min-h-touch w-full border border-border-input bg-surface px-4 text-ink placeholder:text-ink-4"
            />
          )}

          {!keyword && (
            <div className="-mx-gutter px-gutter flex gap-2 overflow-x-auto pb-1">
              {days.map((day) => {
                const active = day.key === dayParam;
                return (
                  <button
                    key={day.key}
                    type="button"
                    onClick={() => update('day', day.key)}
                    aria-pressed={active}
                    className={`rounded-card min-w-[92px] shrink-0 px-4 py-2.5 ${
                      active ? 'bg-ink text-white' : 'border border-border bg-surface text-ink'
                    }`}
                  >
                    <span
                      className={`text-eyebrow tracking-eyebrow block font-bold uppercase ${
                        active ? 'text-white/70' : 'text-ink-4'
                      }`}
                    >
                      {formatShortDay(day.date, timezone)}
                    </span>
                    <span className="text-row mt-1 block font-semibold">
                      {formatMonthDay(day.date, timezone)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {program.data.tracks.length > 0 && (
            <div className="-mx-gutter px-gutter flex gap-2 overflow-x-auto pb-1">
              <FilterChip active={!trackId} onClick={() => update('track', undefined)}>
                All
              </FilterChip>
              {program.data.tracks.map((track) => (
                <FilterChip
                  key={track.id}
                  active={track.id === trackId}
                  onClick={() => update('track', track.id === trackId ? undefined : track.id)}
                >
                  {track.name}
                </FilterChip>
              ))}
            </div>
          )}
        </div>
      </div>

      <main className="px-gutter pt-1 pb-6">
        {!program.data.isPublished && (
          <EmptyBlock title="The agenda is not published yet">
            The organizers are still finalising it. It will appear here as soon as it is ready.
          </EmptyBlock>
        )}

        {program.data.isPublished && visibleDays.length === 0 && (
          <EmptyBlock title="Nothing matches that">
            Try a different keyword, or clear the track filter.
          </EmptyBlock>
        )}

        <div className="space-y-6">
          {visibleDays.map((day) => (
            <section key={day.key}>
              {/* One day at a time needs no heading; a search crossing days does. */}
              {(keyword || visibleDays.length > 1) && (
                <h2 className="text-eyebrow tracking-eyebrow mb-3 font-bold text-ink-3 uppercase">
                  {formatDayHeading(day.date, timezone)}
                </h2>
              )}

              {/* Two to a row on a laptop. The day still reads in order — left, right, then down
                  — and the marker keeps its own line across both, since where the day has got to
                  is a line through the schedule rather than a card in it. */}
              <div className="space-y-card lg:grid lg:grid-cols-2 lg:gap-card lg:space-y-0">
                {withNowMarker(day.sessions, now, day.date === today).map((item) =>
                  item === 'now' ? (
                    <NowMarker key="now" />
                  ) : (
                    <SessionCard key={item.id} session={item} timeZone={timezone} />
                  ),
                )}
              </div>
            </section>
          ))}
        </div>
      </main>
    </>
  );
}

/** Where the day has got to, so the next thing to walk to is found without reading the clock. */
function NowMarker() {
  return (
    <p className="flex items-center gap-2 pt-1 pb-1 lg:col-span-2">
      <span className="h-px w-4 shrink-0 bg-brand" aria-hidden="true" />
      <span className="text-eyebrow tracking-eyebrow font-bold text-brand-text uppercase">
        Happening now
      </span>
      <span className="h-px flex-1 bg-border-3" aria-hidden="true" />
    </p>
  );
}

/**
 * Inserts the marker before the first session still to come. It only appears on the day being
 * lived through, and only once something has started: at 7am on day two it would mark nothing.
 */
function withNowMarker(sessions: Session[], now: Date, isToday: boolean): Array<Session | 'now'> {
  if (!isToday || sessions.length === 0) {
    return sessions;
  }

  const time = now.getTime();
  const first = sessions.at(0);
  const over = sessions.every((session) => Date.parse(session.endsAt) <= time);

  if (!first || Date.parse(first.startsAt) > time || over) {
    return sessions;
  }

  const index = sessions.findIndex((session) => Date.parse(session.startsAt) > time);
  if (index <= 0) {
    return sessions;
  }

  return [...sessions.slice(0, index), 'now', ...sessions.slice(index)];
}
